import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear } from "date-fns";
import { pt } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import {
  Euro, TrendingUp, TrendingDown, ShoppingBag, Calendar as CalendarIcon,
  Plus, Trash2, Car, Package, Scissors, Receipt, Wallet, BarChart3, PiggyBank,
  Users, CreditCard, Banknote, Filter, ExternalLink, ArrowRight, DoorClosed, Clock, Pencil, Percent
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Expense {
  id: string;
  description: string;
  amount: number;
  category: string;
  expense_date: string;
  notes: string;
  created_at: string;
}

interface StaffPayment {
  id: string;
  staff_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  description: string;
  notes: string;
  booking_id: string | null;
  created_at: string;
}

const categoryLabels: Record<string, string> = {
  travel: "Deslocamento",
  products: "Produtos/Material",
  equipment: "Equipamento",
  rent: "Renda/Aluguer",
  marketing: "Marketing",
  salary: "Pagamento Colaborador",
  other: "Outros",
};

const categoryIcons: Record<string, typeof Car> = {
  travel: Car,
  products: Package,
  equipment: Scissors,
  rent: Receipt,
  marketing: TrendingUp,
  salary: Users,
  other: Wallet,
};

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2, 160 60% 45%))",
  "hsl(var(--chart-3, 30 80% 55%))",
  "hsl(var(--chart-4, 280 65% 60%))",
  "hsl(var(--chart-5, 340 75% 55%))",
  "hsl(var(--accent))",
];

type DatePreset = "7d" | "15d" | "1m" | "3m" | "6m" | "1y" | "custom";

const presetLabels: Record<DatePreset, string> = {
  "7d": "7 dias",
  "15d": "15 dias",
  "1m": "1 mês",
  "3m": "Trimestral",
  "6m": "Semestral",
  "1y": "Anual",
  "custom": "Personalizado",
};

const Finances = () => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [staffPayments, setStaffPayments] = useState<StaffPayment[]>([]);
  const [closingLogs, setClosingLogs] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  // Filters
  const [staffFilter, setStaffFilter] = useState("all");
  const [datePreset, setDatePreset] = useState<DatePreset>("1m");
  const [customFrom, setCustomFrom] = useState<Date | undefined>();
  const [customTo, setCustomTo] = useState<Date | undefined>();

  const { toast } = useToast();
  const navigate = useNavigate();

  // Expense form
  const [formDesc, setFormDesc] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formCategory, setFormCategory] = useState("travel");
  const [formDate, setFormDate] = useState(new Date().toISOString().split("T")[0]);
  const [formNotes, setFormNotes] = useState("");

  // Payment form
  const [payStaffId, setPayStaffId] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payDesc, setPayDesc] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().split("T")[0]);
  const [payNotes, setPayNotes] = useState("");

  // Date range calculation
  const dateRange = useMemo(() => {
    const now = new Date();
    let from: Date, to: Date;
    to = now;
    switch (datePreset) {
      case "7d": from = subDays(now, 7); break;
      case "15d": from = subDays(now, 15); break;
      case "1m": from = subMonths(now, 1); break;
      case "3m": from = subMonths(now, 3); break;
      case "6m": from = subMonths(now, 6); break;
      case "1y": from = startOfYear(now); break;
      case "custom":
        from = customFrom || subMonths(now, 1);
        to = customTo || now;
        break;
      default: from = subMonths(now, 1);
    }
    return { from: format(from, "yyyy-MM-dd"), to: format(to, "yyyy-MM-dd") };
  }, [datePreset, customFrom, customTo]);

  const fetchAll = async () => {
    const [bookingsRes, ordersRes, expensesRes, staffRes, paymentsRes, closingsRes] = await Promise.all([
      supabase
        .from("bookings")
        .select("total_price, delivery_fee, status, booking_date, staff_id, services:service_id(name), client_name, discount_amount, discount_by, original_price")
        .in("status", ["confirmed", "completed"])
        .gte("booking_date", dateRange.from)
        .lte("booking_date", dateRange.to)
        .order("booking_date", { ascending: false }),
      supabase
        .from("orders")
        .select("total_amount, status, created_at, delivery_method, discount_amount, discount_by")
        .in("status", ["confirmed", "shipped", "delivered"])
        .order("created_at", { ascending: false }),
      supabase
        .from("expenses")
        .select("*")
        .gte("expense_date", dateRange.from)
        .lte("expense_date", dateRange.to)
        .order("expense_date", { ascending: false }),
      supabase
        .from("staff")
        .select("id, name")
        .eq("is_active", true)
        .order("name"),
      supabase
        .from("staff_payments")
        .select("*")
        .gte("payment_date", dateRange.from)
        .lte("payment_date", dateRange.to)
        .order("payment_date", { ascending: false }),
      supabase
        .from("pdv_logs")
        .select("*")
        .in("action", ["ABERTURA_CAIXA", "FECHO_CAIXA"])
        .gte("created_at", dateRange.from + "T00:00:00")
        .lte("created_at", dateRange.to + "T23:59:59")
        .order("created_at", { ascending: false }),
    ]);

    setBookings(bookingsRes.data || []);
    setOrders(ordersRes.data || []);
    setExpenses((expensesRes.data as Expense[]) || []);
    setStaffList((staffRes.data as { id: string; name: string }[]) || []);
    setStaffPayments((paymentsRes.data as StaffPayment[]) || []);
    setClosingLogs(closingsRes.data || []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [dateRange.from, dateRange.to]);

  // Apply staff filter
  const filteredBookings = useMemo(() =>
    staffFilter === "all" ? bookings : bookings.filter(b => b.staff_id === staffFilter),
    [bookings, staffFilter]);
  const filteredOrders = useMemo(() => {
    const inRange = orders.filter(o => {
      const d = o.created_at?.substring(0, 10);
      return d >= dateRange.from && d <= dateRange.to;
    });
    return inRange;
  }, [orders, dateRange]);
  const filteredExpenses = expenses;
  const filteredPayments = useMemo(() =>
    staffFilter === "all" ? staffPayments : staffPayments.filter(p => p.staff_id === staffFilter),
    [staffPayments, staffFilter]);

  // Stats
  const stats = useMemo(() => {
    const bookingRevenue = filteredBookings.reduce((s, b) => s + Number(b.total_price), 0);
    const deliveryFees = filteredBookings.reduce((s, b) => s + Number(b.delivery_fee), 0);
    const orderRevenue = filteredOrders.reduce((s, o) => s + Number(o.total_amount), 0);
    const totalRevenue = bookingRevenue + orderRevenue + deliveryFees;
    const travelExpenses = filteredExpenses.filter(e => e.category === "travel").reduce((s, e) => s + Number(e.amount), 0);
    const totalExpenses = filteredExpenses.reduce((s, e) => s + Number(e.amount), 0);
    const totalPayments = filteredPayments.reduce((s, p) => s + Number(p.amount), 0);
    const totalDiscounts = filteredBookings.reduce((s, b) => s + Number(b.discount_amount || 0), 0)
      + filteredOrders.reduce((s, o) => s + Number(o.discount_amount || 0), 0);
    const profit = totalRevenue - totalExpenses - totalPayments;
    const travelProfit = deliveryFees - travelExpenses;
    const bookingCount = filteredBookings.length;
    const orderCount = filteredOrders.length;
    const transactions = bookingCount + orderCount;
    const avgTicket = transactions ? (bookingRevenue + deliveryFees + orderRevenue) / transactions : 0;
    const avgBookingTicket = bookingCount ? (bookingRevenue + deliveryFees) / bookingCount : 0;
    const avgOrderTicket = orderCount ? orderRevenue / orderCount : 0;
    return { bookingRevenue, deliveryFees, orderRevenue, totalRevenue, totalDiscounts, totalExpenses, totalPayments, profit, travelExpenses, travelProfit, bookingCount, orderCount, transactions, avgTicket, avgBookingTicket, avgOrderTicket };
  }, [filteredBookings, filteredOrders, filteredExpenses, filteredPayments]);

  // Histórico mensal de ticket médio (independente do filtro de datas acima)
  const [ticketMonths, setTicketMonths] = useState("12");
  const [ticketBookings, setTicketBookings] = useState<any[]>([]);
  const [ticketOrders, setTicketOrders] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      const from = format(startOfMonth(subMonths(new Date(), parseInt(ticketMonths, 10) - 1)), "yyyy-MM-dd");
      const [b, o] = await Promise.all([
        supabase.from("bookings").select("total_price, delivery_fee, booking_date, staff_id")
          .in("status", ["confirmed", "completed"]).gte("booking_date", from),
        supabase.from("orders").select("total_amount, created_at")
          .in("status", ["confirmed", "shipped", "delivered"]).gte("created_at", from + "T00:00:00"),
      ]);
      setTicketBookings(b.data || []);
      setTicketOrders(o.data || []);
    };
    load();
  }, [ticketMonths]);

  const monthlyTicket = useMemo(() => {
    const map: Record<string, { mes: string; receita: number; pedidos: number }> = {};
    const n = parseInt(ticketMonths, 10);
    for (let i = n - 1; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const key = format(d, "yyyy-MM");
      map[key] = { mes: format(d, "MMM/yy", { locale: pt }), receita: 0, pedidos: 0 };
    }
    const src = staffFilter === "all" ? ticketBookings : ticketBookings.filter((b) => b.staff_id === staffFilter);
    for (const b of src) {
      const key = String(b.booking_date || "").slice(0, 7);
      if (!map[key]) continue;
      map[key].receita += Number(b.total_price || 0) + Number(b.delivery_fee || 0);
      map[key].pedidos += 1;
    }
    if (staffFilter === "all") {
      for (const o of ticketOrders) {
        const key = String(o.created_at || "").slice(0, 7);
        if (!map[key]) continue;
        map[key].receita += Number(o.total_amount || 0);
        map[key].pedidos += 1;
      }
    }
    return Object.keys(map).sort().map((k) => ({
      ...map[k],
      ticket: map[k].pedidos ? map[k].receita / map[k].pedidos : 0,
    }));
  }, [ticketBookings, ticketOrders, ticketMonths, staffFilter]);


  // Chart data: revenue by day
  const revenueChartData = useMemo(() => {
    const map: Record<string, { date: string; servicos: number; loja: number; deslocamento: number }> = {};
    filteredBookings.forEach(b => {
      const d = b.booking_date;
      if (!map[d]) map[d] = { date: d, servicos: 0, loja: 0, deslocamento: 0 };
      map[d].servicos += Number(b.total_price);
      map[d].deslocamento += Number(b.delivery_fee);
    });
    filteredOrders.forEach(o => {
      const d = o.created_at?.substring(0, 10);
      if (!map[d]) map[d] = { date: d, servicos: 0, loja: 0, deslocamento: 0 };
      map[d].loja += Number(o.total_amount);
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredBookings, filteredOrders]);

  // Displacement bookings (with delivery_fee > 0)
  const displacementBookings = useMemo(() =>
    filteredBookings.filter(b => Number(b.delivery_fee) > 0),
    [filteredBookings]);
  const travelExpensesList = useMemo(() =>
    filteredExpenses.filter(e => e.category === "travel"),
    [filteredExpenses]);

  // Chart data: expenses by category
  const expensePieData = useMemo(() => {
    const map: Record<string, number> = {};
    filteredExpenses.forEach(e => {
      map[e.category] = (map[e.category] || 0) + Number(e.amount);
    });
    return Object.entries(map).map(([k, v]) => ({ name: categoryLabels[k] || k, value: v }));
  }, [filteredExpenses]);

  // Staff revenue breakdown
  const staffRevenueData = useMemo(() => {
    const map: Record<string, number> = {};
    bookings.forEach(b => {
      const name = staffList.find(s => s.id === b.staff_id)?.name || "Sem colaborador";
      map[name] = (map[name] || 0) + Number(b.total_price);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [bookings, staffList]);

  // Cash register closings, pair opening with closing
  const registerClosings = useMemo(() => {
    const openings = closingLogs.filter((l: any) => l.action === "ABERTURA_CAIXA").sort((a: any, b: any) => a.created_at.localeCompare(b.created_at));
    const closings = closingLogs.filter((l: any) => l.action === "FECHO_CAIXA").sort((a: any, b: any) => a.created_at.localeCompare(b.created_at));

    // Match closings to openings by same user, closest time
    const paired: any[] = [];
    const usedOpenings = new Set<number>();

    closings.forEach((closing: any) => {
      // Find nearest opening before this closing by same user
      let bestIdx = -1;
      for (let i = openings.length - 1; i >= 0; i--) {
        if (usedOpenings.has(i)) continue;
        if (openings[i].user_id === closing.user_id && openings[i].created_at < closing.created_at) {
          bestIdx = i;
          break;
        }
      }
      const opening = bestIdx >= 0 ? openings[bestIdx] : null;
      if (bestIdx >= 0) usedOpenings.add(bestIdx);

      const d = closing.details || {};
      paired.push({
        id: closing.id,
        closedAt: closing.created_at,
        openedAt: opening?.created_at || null,
        openedBy: opening?.user_name || opening?.user_email || "-",
        closedBy: closing.user_name || closing.user_email,
        openingAmount: d.opening_amount ?? opening?.details?.amount ?? 0,
        cashCounted: d.cash_counted ?? 0,
        cardCounted: d.card_counted ?? 0,
        mbwayCounted: d.mbway_counted ?? 0,
        multibancoCounted: d.multibanco_counted ?? 0,
        totalCounted: d.total_counted ?? 0,
        expectedTotal: d.expected_total ?? 0,
        difference: d.difference ?? 0,
        matches: d.matches ?? false,
      });
    });

    return paired.sort((a, b) => b.closedAt.localeCompare(a.closedAt));
  }, [closingLogs]);

  // Expense CRUD
  const openNewExpense = () => {
    setEditingExpense(null);
    setFormDesc(""); setFormAmount(""); setFormCategory("travel");
    setFormDate(new Date().toISOString().split("T")[0]); setFormNotes("");
    setExpenseDialogOpen(true);
  };
  const openEditExpense = (e: Expense) => {
    setEditingExpense(e);
    setFormDesc(e.description); setFormAmount(String(e.amount)); setFormCategory(e.category);
    setFormDate(e.expense_date); setFormNotes(e.notes || "");
    setExpenseDialogOpen(true);
  };
  const saveExpense = async () => {
    if (!formDesc || !formAmount) { toast({ title: "Preencha descrição e valor", variant: "destructive" }); return; }
    const payload = { description: formDesc, amount: parseFloat(formAmount), category: formCategory, expense_date: formDate, notes: formNotes, updated_at: new Date().toISOString() };
    if (editingExpense) {
      await supabase.from("expenses").update(payload).eq("id", editingExpense.id);
      toast({ title: "Despesa atualizada!" });
    } else {
      await supabase.from("expenses").insert(payload);
      toast({ title: "Despesa adicionada!" });
    }
    setExpenseDialogOpen(false); fetchAll();
  };
  const deleteExpense = async (id: string) => {
    if (!confirm("Eliminar esta despesa?")) return;
    await supabase.from("expenses").delete().eq("id", id);
    toast({ title: "Despesa eliminada" }); fetchAll();
  };

  // Staff Payment CRUD
  const openNewPayment = () => {
    setPayStaffId(staffList[0]?.id || ""); setPayAmount(""); setPayMethod("cash");
    setPayDesc(""); setPayDate(new Date().toISOString().split("T")[0]); setPayNotes("");
    setPaymentDialogOpen(true);
  };
  const savePayment = async () => {
    if (!payStaffId || !payAmount) { toast({ title: "Preencha colaborador e valor", variant: "destructive" }); return; }
    await supabase.from("staff_payments").insert({
      staff_id: payStaffId, amount: parseFloat(payAmount), payment_method: payMethod,
      description: payDesc, payment_date: payDate, notes: payNotes,
    });
    toast({ title: "Pagamento registado!" });
    setPaymentDialogOpen(false); fetchAll();
  };
  const deletePayment = async (id: string) => {
    if (!confirm("Eliminar este pagamento?")) return;
    await supabase.from("staff_payments").delete().eq("id", id);
    toast({ title: "Pagamento eliminado" }); fetchAll();
  };

  const getStaffName = (id: string) => staffList.find(s => s.id === id)?.name || "-";

  // Closing management
  const [editClosingDialog, setEditClosingDialog] = useState(false);
  const [editingClosing, setEditingClosing] = useState<any>(null);
  const [closingNotes, setClosingNotes] = useState("");

  const deleteClosing = async (closingId: string) => {
    await supabase.from("pdv_logs").delete().eq("id", closingId);
    toast({ title: "Fechamento eliminado" });
    fetchAll();
  };

  const deleteAllClosings = async () => {
    const ids = closingLogs.map((l: any) => l.id);
    if (ids.length === 0) return;
    await supabase.from("pdv_logs").delete().in("id", ids);
    toast({ title: "Todos os fechamentos do período foram eliminados" });
    fetchAll();
  };

  const openEditClosing = (c: any) => {
    setEditingClosing(c);
    setClosingNotes("");
    setEditClosingDialog(true);
  };

  if (loading) return <p className="text-muted-foreground p-8">A carregar...</p>;

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Financeiro</h1>
          <p className="text-muted-foreground">Visão completa das finanças do negócio</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Staff filter */}
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger className="w-44 rounded-full">
              <Users size={14} className="mr-2" />
              <SelectValue placeholder="Colaborador" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {staffList.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date preset */}
          <Select value={datePreset} onValueChange={(v) => setDatePreset(v as DatePreset)}>
            <SelectTrigger className="w-40 rounded-full">
              <CalendarIcon size={14} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(presetLabels).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Custom date range */}
          {datePreset === "custom" && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full text-xs gap-1">
                    <CalendarIcon size={12} />
                    {customFrom ? format(customFrom, "dd/MM/yy") : "De"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customFrom} onSelect={setCustomFrom}
                    className={cn("p-3 pointer-events-auto")} locale={pt} />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground text-xs">&gt;</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full text-xs gap-1">
                    <CalendarIcon size={12} />
                    {customTo ? format(customTo, "dd/MM/yy") : "Até"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customTo} onSelect={setCustomTo}
                    className={cn("p-3 pointer-events-auto")} locale={pt} />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* PDV link */}
          <Button variant="outline" className="rounded-full gap-2" onClick={() => navigate("/pdv")}>
            <CreditCard size={14} /> Caixa (PDV) <ExternalLink size={12} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {[
          { label: "Receita Total", value: stats.totalRevenue, icon: Euro, color: "bg-emerald-500/10 text-emerald-600" },
          { label: "Serviços", value: stats.bookingRevenue, icon: Scissors, color: "bg-blue-500/10 text-blue-600" },
          { label: "Loja", value: stats.orderRevenue, icon: ShoppingBag, color: "bg-violet-500/10 text-violet-600" },
          { label: "Descontos", value: stats.totalDiscounts, icon: Percent, color: "bg-amber-500/10 text-amber-600", negative: true },
          { label: "Despesas", value: stats.totalExpenses, icon: TrendingDown, color: "bg-red-500/10 text-red-600", negative: true },
          { label: "Pago Colab.", value: stats.totalPayments, icon: Users, color: "bg-orange-500/10 text-orange-600", negative: true },
          { label: "Lucro", value: stats.profit, icon: PiggyBank, color: stats.profit >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600" },
        ].map((c, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.color}`}>
                  <c.icon size={15} />
                </div>
                <span className="text-[11px] text-muted-foreground leading-tight">{c.label}</span>
              </div>
              <p className={`text-lg font-bold ${c.negative ? "text-red-600" : "text-foreground"}`}>
                {c.negative ? "-" : ""}€{Math.abs(c.value).toFixed(2)}
              </p>
            </CardContent>
          </Card>
        ))}
        {/* Deslocamentos card with dual info */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-500/10">
                <Car size={15} className="text-amber-600" />
              </div>
              <span className="text-[11px] text-muted-foreground leading-tight">Deslocamentos</span>
            </div>
            <p className="text-lg font-bold text-foreground">€{stats.deliveryFees.toFixed(2)}</p>
            <p className="text-[10px] text-emerald-600 mt-0.5">Pago pelo cliente</p>
            <div className="border-t border-border mt-2 pt-2">
              <p className="text-sm font-bold text-red-600">-€{stats.travelExpenses.toFixed(2)}</p>
              <p className="text-[10px] text-red-500">Custo da empresa</p>
            </div>
            <div className="border-t border-border mt-2 pt-1.5">
              <p className={`text-xs font-bold ${stats.travelProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {stats.travelProfit >= 0 ? "+" : ""}€{stats.travelProfit.toFixed(2)}
              </p>
              <p className="text-[10px] text-muted-foreground">{stats.travelProfit > 0 ? "Lucro" : stats.travelProfit < 0 ? "Prejuízo" : "Empatado"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ticket Médio */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Percent size={15} className="text-primary" /> Ticket Médio
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => navigate("/admin/reports")}>
                <Users size={14} /> Relatórios & CRM
              </Button>
              <Select value={ticketMonths} onValueChange={setTicketMonths}>
                <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 meses</SelectItem>
                  <SelectItem value="6">6 meses</SelectItem>
                  <SelectItem value="12">12 meses</SelectItem>
                  <SelectItem value="24">24 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Ticket médio geral", value: `€${stats.avgTicket.toFixed(2)}`, hint: `${stats.transactions} pedidos no período` },
              { label: "Ticket médio serviços", value: `€${stats.avgBookingTicket.toFixed(2)}`, hint: `${stats.bookingCount} reservas` },
              { label: "Ticket médio loja", value: `€${stats.avgOrderTicket.toFixed(2)}`, hint: `${stats.orderCount} encomendas` },
              { label: "Pedidos no período", value: String(stats.transactions), hint: `€${stats.totalRevenue.toFixed(2)} faturados` },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-border p-3">
                <p className="text-[11px] text-muted-foreground">{c.label}</p>
                <p className="text-lg font-bold text-foreground">{c.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{c.hint}</p>
              </div>
            ))}
          </div>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyTicket}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="mes" fontSize={11} />
                <YAxis fontSize={11} />
                <RTooltip
                  formatter={(v: any, n: any) =>
                    n === "pedidos" ? [v, "Pedidos"] : [`€${Number(v).toFixed(2)}`, n === "ticket" ? "Ticket médio" : "Receita"]
                  }
                />
                <Legend />
                <Bar dataKey="ticket" name="Ticket médio" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="pedidos" name="Pedidos" fill="hsl(var(--muted-foreground))" opacity={0.4} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mês</TableHead>
                  <TableHead className="text-right">Pedidos</TableHead>
                  <TableHead className="text-right">Receita</TableHead>
                  <TableHead className="text-right">Ticket médio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyTicket.map((m) => (
                  <TableRow key={m.mes}>
                    <TableCell className="capitalize">{m.mes}</TableCell>
                    <TableCell className="text-right">{m.pedidos}</TableCell>
                    <TableCell className="text-right">€{m.receita.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">€{m.ticket.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>



      {/* Charts */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Revenue over time */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 size={15} className="text-primary" /> Receitas por Dia</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => format(new Date(v), "dd/MM")} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RTooltip formatter={(v: number) => `€${v.toFixed(2)}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="servicos" name="Serviços" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="deslocamento" name="Deslocamento" fill="hsl(var(--chart-3, 30 80% 55%))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="loja" name="Loja" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-12 text-sm">Sem dados no período selecionado</p>
            )}
          </CardContent>
        </Card>

        {/* Expenses pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingDown size={15} className="text-destructive" /> Despesas</CardTitle>
          </CardHeader>
          <CardContent>
            {expensePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={expensePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {expensePieData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip formatter={(v: number) => `€${v.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-12 text-sm">Sem despesas</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Staff revenue chart */}
      {staffRevenueData.length > 0 && staffFilter === "all" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Users size={15} className="text-primary" /> Receita por Colaborador</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={staffRevenueData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                <RTooltip formatter={(v: number) => `€${v.toFixed(2)}`} />
                <Bar dataKey="value" name="Receita" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="bookings">
        <TabsList className="flex-wrap">
          <TabsTrigger value="bookings">Serviços ({filteredBookings.length})</TabsTrigger>
          <TabsTrigger value="displacement">Deslocamentos ({displacementBookings.length})</TabsTrigger>
          <TabsTrigger value="orders">Loja ({filteredOrders.length})</TabsTrigger>
          <TabsTrigger value="expenses">Despesas ({filteredExpenses.length})</TabsTrigger>
          <TabsTrigger value="payments">Pagamentos Colab. ({filteredPayments.length})</TabsTrigger>
          <TabsTrigger value="closings">Fechamentos ({registerClosings.length})</TabsTrigger>
        </TabsList>

        {/* Bookings */}
        <TabsContent value="bookings" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => navigate("/admin/agenda")}>
              <CalendarIcon size={14} /> Ir para Agenda <ArrowRight size={12} />
            </Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Serviço</TableHead>
                  <TableHead>Colaborador</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sem sessões neste período.</TableCell></TableRow>
                ) : filteredBookings.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{b.booking_date}</TableCell>
                    <TableCell className="text-sm">
                      <button 
                        onClick={() => {
                          // Navegar para a ficha do cliente. Passamos o email e telefone para garantir que encontramos as reservas 
                          // mesmo que o cliente não tenha uma ficha criada.
                          const params = new URLSearchParams();
                          params.set("search", b.client_name);
                          if (b.client_email) params.set("email", b.client_email);
                          if (b.client_phone) params.set("phone", b.client_phone);
                          navigate(`/admin/clients?${params.toString()}`);
                        }}
                        className="hover:text-primary hover:underline transition-colors"
                      >
                        {b.client_name || "-"}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm">{(b.services as any)?.name || "-"}</TableCell>
                    <TableCell className="text-sm">{getStaffName(b.staff_id)}</TableCell>
                    <TableCell className="text-right font-medium text-sm">€{Number(b.total_price).toFixed(2)}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{b.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Deslocamentos */}
        <TabsContent value="displacement" className="mt-4 space-y-4">
          {/* Profitability Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-emerald-500/10">
                    <Car size={15} className="text-emerald-600" />
                  </div>
                  <span className="text-[11px] text-muted-foreground">Receita Deslocamentos</span>
                </div>
                <p className="text-lg font-bold text-foreground">€{stats.deliveryFees.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-red-500/10">
                    <TrendingDown size={15} className="text-red-600" />
                  </div>
                  <span className="text-[11px] text-muted-foreground">Despesas Deslocamento</span>
                </div>
                <p className="text-lg font-bold text-red-600">-€{stats.travelExpenses.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${stats.travelProfit >= 0 ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                    <PiggyBank size={15} className={stats.travelProfit >= 0 ? "text-emerald-600" : "text-red-600"} />
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {stats.travelProfit > 0 ? "Lucro" : stats.travelProfit < 0 ? "Prejuízo" : "Empatado"}
                  </span>
                </div>
                <p className={`text-lg font-bold ${stats.travelProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {stats.travelProfit >= 0 ? "+" : ""}€{stats.travelProfit.toFixed(2)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue: bookings with displacement */}
          <div>
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Car size={14} className="text-primary" /> Receita de Deslocamentos (automático das reservas)</h3>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Serviço</TableHead>
                    <TableHead>Colaborador</TableHead>
                    <TableHead className="text-right">Taxa Desloc.</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displacementBookings.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sem deslocamentos neste período.</TableCell></TableRow>
                  ) : displacementBookings.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{b.booking_date}</TableCell>
                      <TableCell className="text-sm">{b.client_name || "-"}</TableCell>
                      <TableCell className="text-sm">{(b.services as any)?.name || "-"}</TableCell>
                      <TableCell className="text-sm">{getStaffName(b.staff_id)}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-600 text-sm">+€{Number(b.delivery_fee).toFixed(2)}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{b.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Expenses: travel category */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2"><TrendingDown size={14} className="text-destructive" /> Despesas de Deslocamento</h3>
              <Button size="sm" className="rounded-full gap-1 text-xs" onClick={() => { setFormCategory("travel"); openNewExpense(); }}>
                <Plus size={14} /> Registar Despesa
              </Button>
            </div>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Notas</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {travelExpensesList.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sem despesas de deslocamento. Registe combustível, portagens, etc.</TableCell></TableRow>
                  ) : travelExpensesList.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm">{new Date(e.expense_date).toLocaleDateString("pt-PT")}</TableCell>
                      <TableCell className="font-medium text-sm">{e.description}</TableCell>
                      <TableCell className="text-right font-medium text-red-600 text-sm">-€{Number(e.amount).toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{e.notes || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditExpense(e)}>Editar</Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteExpense(e.id)}><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Método Entrega</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Sem vendas neste período.</TableCell></TableRow>
                ) : filteredOrders.map((o, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{new Date(o.created_at).toLocaleDateString("pt-PT")}</TableCell>
                    <TableCell className="text-sm">{o.delivery_method === "pickup" ? "Retirada" : o.delivery_method === "massage_pickup" ? "Na massagem" : "Entrega"}</TableCell>
                    <TableCell className="text-right font-medium text-sm">€{Number(o.total_amount).toFixed(2)}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{o.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Expenses */}
        <TabsContent value="expenses" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full gap-2" onClick={openNewExpense}><Plus size={16} /> Adicionar Despesa</Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sem despesas neste período.</TableCell></TableRow>
                ) : filteredExpenses.map(e => {
                  const Icon = categoryIcons[e.category] || Wallet;
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm">{new Date(e.expense_date).toLocaleDateString("pt-PT")}</TableCell>
                      <TableCell className="font-medium text-sm">{e.description}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs gap-1"><Icon size={12} /> {categoryLabels[e.category] || e.category}</Badge></TableCell>
                      <TableCell className="text-right font-medium text-red-600 text-sm">-€{Number(e.amount).toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditExpense(e)}>Editar</Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteExpense(e.id)}><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Staff Payments */}
        <TabsContent value="payments" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full gap-2" onClick={openNewPayment}><Plus size={16} /> Registar Pagamento</Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sem pagamentos registados.</TableCell></TableRow>
                ) : filteredPayments.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm">{new Date(p.payment_date).toLocaleDateString("pt-PT")}</TableCell>
                    <TableCell className="text-sm font-medium">{getStaffName(p.staff_id)}</TableCell>
                    <TableCell className="text-sm">{p.description || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {p.payment_method === "cash" ? "Dinheiro" : p.payment_method === "transfer" ? "Transferência" : p.payment_method === "mbway" ? "MB Way" : p.payment_method}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium text-sm">€{Number(p.amount).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deletePayment(p.id)}><Trash2 size={14} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        {/* Fechamentos */}
        <TabsContent value="closings" className="mt-4 space-y-4">
          {registerClosings.length > 0 && (
            <div className="flex justify-end gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="rounded-full gap-2">
                    <Trash2 size={14} /> Limpar todos
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminar todos os fechamentos?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Isto irá eliminar permanentemente todos os registos de fechamento de caixa do período selecionado. Esta ação não pode ser revertida.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteAllClosings}>Eliminar Todos</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          <Card>
            {registerClosings.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <DoorClosed size={32} className="mx-auto mb-3 opacity-40" />
                <p>Nenhum fechamento de caixa no período selecionado.</p>
              </div>
            ) : (
              <div className="space-y-4 p-4">
                {registerClosings.map((c: any) => {
                  const diffColor = c.matches
                    ? "text-emerald-600"
                    : c.difference > 0
                    ? "text-amber-600"
                    : "text-red-600";
                  return (
                    <div key={c.id} className="rounded-xl border border-border p-5 space-y-4">
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.matches ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                            <DoorClosed size={18} className={c.matches ? "text-emerald-600" : "text-red-600"} />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">Fecho de Caixa</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock size={11} />
                              {format(new Date(c.closedAt), "dd/MM/yyyy 'às' HH:mm", { locale: pt })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={c.matches ? "default" : "destructive"} className="rounded-full w-fit">
                            {c.matches ? "Valores corretos" : `Diferença: €${Math.abs(c.difference).toFixed(2)}`}
                          </Badge>
                          <Button size="icon" variant="ghost" onClick={() => openEditClosing(c)} title="Ver detalhes">
                            <Pencil size={14} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-destructive" title="Eliminar">
                                <Trash2 size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar este fechamento?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  O registo de fecho de caixa de {format(new Date(c.closedAt), "dd/MM/yyyy HH:mm")} será eliminado permanentemente.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteClosing(c.id)}>Eliminar</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>

                      {/* Users */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-muted/50 p-3">
                          <p className="text-xs text-muted-foreground mb-1">Aberto por</p>
                          <p className="font-medium text-foreground">{c.openedBy}</p>
                          {c.openedAt && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {format(new Date(c.openedAt), "dd/MM/yy HH:mm", { locale: pt })}
                            </p>
                          )}
                        </div>
                        <div className="rounded-lg bg-muted/50 p-3">
                          <p className="text-xs text-muted-foreground mb-1">Fechado por</p>
                          <p className="font-medium text-foreground">{c.closedBy}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {format(new Date(c.closedAt), "dd/MM/yy HH:mm", { locale: pt })}
                          </p>
                        </div>
                      </div>

                      {/* Values */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div className="rounded-lg border border-border p-3">
                          <p className="text-xs text-muted-foreground">Fundo de caixa</p>
                          <p className="font-bold text-foreground">€{Number(c.openingAmount).toFixed(2)}</p>
                        </div>
                        <div className="rounded-lg border border-border p-3">
                          <p className="text-xs text-muted-foreground">Contado (total)</p>
                          <p className="font-bold text-foreground">€{Number(c.totalCounted).toFixed(2)}</p>
                        </div>
                        <div className="rounded-lg border border-border p-3">
                          <p className="text-xs text-muted-foreground">Esperado</p>
                          <p className="font-bold text-foreground">€{Number(c.expectedTotal).toFixed(2)}</p>
                        </div>
                        <div className="rounded-lg border border-border p-3">
                          <p className="text-xs text-muted-foreground">Diferença</p>
                          <p className={`font-bold ${diffColor}`}>
                            {c.difference >= 0 ? "+" : ""}€{Number(c.difference).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Breakdown */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div className="flex justify-between bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">Dinheiro</span>
                          <span className="font-medium text-foreground">€{Number(c.cashCounted).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">Cartão</span>
                          <span className="font-medium text-foreground">€{Number(c.cardCounted).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">MB Way</span>
                          <span className="font-medium text-foreground">€{Number(c.mbwayCounted).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">Multibanco</span>
                          <span className="font-medium text-foreground">€{Number(c.multibancoCounted).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">{editingExpense ? "Editar Despesa" : "Nova Despesa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Descrição *</Label><Input value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Ex: Combustível ida e volta" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor (€) *</Label><Input type="number" step="0.01" value={formAmount} onChange={e => setFormAmount(e.target.value)} placeholder="0.00" /></div>
              <div><Label>Data *</Label><Input type="date" value={formDate} onChange={e => setFormDate(e.target.value)} /></div>
            </div>
            <div><Label>Categoria</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(categoryLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Notas</Label><Textarea value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Observações (opcional)" rows={2} /></div>
            <Button className="w-full rounded-full" onClick={saveExpense}>{editingExpense ? "Guardar Alterações" : "Adicionar Despesa"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Registar Pagamento a Colaborador</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Colaborador *</Label>
              <Select value={payStaffId} onValueChange={setPayStaffId}>
                <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>{staffList.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor (€) *</Label><Input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="0.00" /></div>
              <div><Label>Data *</Label><Input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} /></div>
            </div>
            <div><Label>Método de Pagamento</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Dinheiro</SelectItem>
                  <SelectItem value="transfer">Transferência</SelectItem>
                  <SelectItem value="mbway">MB Way</SelectItem>
                  <SelectItem value="check">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Descrição</Label><Input value={payDesc} onChange={e => setPayDesc(e.target.value)} placeholder="Ex: Pagamento semanal" /></div>
            <div><Label>Notas</Label><Textarea value={payNotes} onChange={e => setPayNotes(e.target.value)} placeholder="Observações" rows={2} /></div>
            <Button className="w-full rounded-full" onClick={savePayment}>Registar Pagamento</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Closing Detail/Edit Dialog */}
      <Dialog open={editClosingDialog} onOpenChange={setEditClosingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Detalhes do Fechamento</DialogTitle>
          </DialogHeader>
          {editingClosing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Fechado em</Label>
                  <p className="font-medium">{format(new Date(editingClosing.closedAt), "dd/MM/yyyy HH:mm", { locale: pt })}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Fechado por</Label>
                  <p className="font-medium">{editingClosing.closedBy}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Fundo de caixa</Label>
                  <p className="font-bold">€{Number(editingClosing.openingAmount).toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total contado</Label>
                  <p className="font-bold">€{Number(editingClosing.totalCounted).toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Esperado</Label>
                  <p className="font-bold">€{Number(editingClosing.expectedTotal).toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Diferença</Label>
                  <p className={`font-bold ${editingClosing.matches ? "text-emerald-600" : "text-red-600"}`}>
                    {editingClosing.difference >= 0 ? "+" : ""}€{Number(editingClosing.difference).toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="border-t border-border pt-3 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Dinheiro:</span> <span className="font-medium">€{Number(editingClosing.cashCounted).toFixed(2)}</span></div>
                <div><span className="text-muted-foreground">Cartão:</span> <span className="font-medium">€{Number(editingClosing.cardCounted).toFixed(2)}</span></div>
                <div><span className="text-muted-foreground">MB Way:</span> <span className="font-medium">€{Number(editingClosing.mbwayCounted).toFixed(2)}</span></div>
                <div><span className="text-muted-foreground">Multibanco:</span> <span className="font-medium">€{Number(editingClosing.multibancoCounted).toFixed(2)}</span></div>
              </div>
              <div className="border-t border-border pt-3">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full rounded-full gap-2">
                      <Trash2 size={14} /> Eliminar este fechamento
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar eliminação?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Este registo de fecho de caixa será eliminado permanentemente.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => { deleteClosing(editingClosing.id); setEditClosingDialog(false); }}>
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Finances;
